import * as React from 'react';
import { cva } from 'class-variance-authority';
import { AlertTriangle, CheckCircle2, Circle, Info, XCircle, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The five status tones the backend's ~70 values across 17 status vocabularies
 * collapse into (see `docs/FRONTEND_COMPONENT_INVENTORY.md` §2.2 and §5.1).
 */
export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

/**
 * The shape a status mapper produces, declared **once** (contract §5.1).
 *
 * It lives here, beside `StatusBadge`, because `StatusBadge` is what consumes
 * the type: the mappers only produce it. The inventory plans for 17 status
 * vocabularies; with the interface declared in each mapper it would be copied
 * seventeen times, and the day somebody adds a field they would add it to one
 * only. The mappers import it from here.
 *
 * `icon` is mandatory on purpose, even though `StatusBadgeProps['icon']` is
 * optional (it defaults per tone): a domain mapper always picks the glyph that
 * names ITS status, because the tone default only says "what kind of verdict",
 * not which one. It is the non-colour half of the icon+text pair (WCAG 1.4.1).
 */
export interface StatusPresentation {
  tone: StatusTone;
  variant: 'outline' | 'soft';
  /** es-CL */
  label: string;
  icon: LucideIcon;
}

/**
 * A pure primitive: it knows no domain status. The status →
 * `{ tone, variant, label, icon }` mapping lives in each feature's mapper
 * (`features/dtes/dte-status-map.ts`, `features/envios/envio-status-map.ts`, …).
 */
const statusBadgeVariants = cva(
  // The border is always reserved (`border` + width), even though `soft` paints
  // it transparent: that way the badge does not change size between variants.
  // Spacing by semantic role, just like `callout.tsx` / `empty-state.tsx` /
  // `error-state.tsx`: if `--semantic-space-*` moves, the badge moves with the
  // rest instead of staying nailed to the numeric scale.
  // `gap-stack-sm` is the 4px role (`--semantic-space-stack-sm`), the same value
  // `gap-1` had.
  // `py-inset-xs` is the 2px role (`--semantic-space-inset-xs`), added to
  // `@tcit/tokens` precisely because the badge was the one component still on
  // Tailwind's numeric scale: `py-stack-sm` (4px) fattens it to double and there
  // was nothing semantic below it.
  // `whitespace-nowrap` (S-7): a two-word status like «Aceptado con reparos»
  // otherwise wraps inside a narrow table cell, which reads as a rendering bug
  // rather than a long label. The column that hosts it is sized to fit instead
  // (`DtesListPage`'s status column, `w-56`), so this never clips the text.
  'inline-flex items-center gap-stack-sm whitespace-nowrap rounded-full border px-inset-sm py-inset-xs text-caption font-medium',
  {
    variants: {
      tone: {
        neutral: '',
        info: '',
        success: '',
        warning: '',
        danger: '',
      },
      variant: {
        // `outline` = in transit, nothing decided yet: transparent background,
        // border and text in the tone's colour.
        outline: 'bg-transparent',
        // `soft` = there is a verdict: the tone's faint background, no visible
        // border.
        soft: 'border-transparent',
      },
    },
    compoundVariants: [
      { tone: 'neutral', variant: 'outline', class: 'border-border-strong text-fg-muted' },
      { tone: 'neutral', variant: 'soft', class: 'bg-subtle text-fg' },
      { tone: 'info', variant: 'outline', class: 'border-info text-info' },
      { tone: 'info', variant: 'soft', class: 'bg-info-subtle text-info' },
      { tone: 'success', variant: 'outline', class: 'border-success text-success' },
      { tone: 'success', variant: 'soft', class: 'bg-success-subtle text-success' },
      { tone: 'warning', variant: 'outline', class: 'border-warning text-warning' },
      { tone: 'warning', variant: 'soft', class: 'bg-warning-subtle text-warning' },
      { tone: 'danger', variant: 'outline', class: 'border-danger text-danger' },
      { tone: 'danger', variant: 'soft', class: 'bg-danger-subtle text-danger' },
    ],
    defaultVariants: {
      variant: 'soft',
    },
  },
);

/**
 * The glyph a badge falls back to when the caller passes none (R8(a)): every
 * tone has its own silhouette, so a badge is never colour alone (WCAG 1.4.1).
 */
const DEFAULT_TONE_ICON: Record<StatusTone, LucideIcon> = {
  neutral: Circle,
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
};

export interface StatusBadgeProps {
  tone: StatusTone;
  /** `outline` = in transit, nothing decided yet. `soft` = there is a verdict. */
  variant?: 'outline' | 'soft';
  /**
   * `undefined` (the default) renders the tone's own icon; an explicit icon
   * wins; `null` is the documented opt-out for the rare badge whose label
   * already carries its own glyph. No caller uses it today.
   */
  icon?: LucideIcon | null;
  children: React.ReactNode;
  className?: string;
}

/**
 * A status badge. It never communicates by colour alone (WCAG 1.4.1): with no
 * `icon` it shows the tone's default (neutral Circle, info Info, success
 * CheckCircle2, warning AlertTriangle, danger XCircle). Every domain mapper
 * (`DTE_STATUS`, `ENVIO_STATUS`) still brings its own, which is why
 * `StatusPresentation.icon` is mandatory there. `icon={null}` opts out.
 */
export function StatusBadge({
  tone,
  variant,
  icon,
  children,
  className,
}: StatusBadgeProps): React.JSX.Element {
  const Icon = icon === undefined ? DEFAULT_TONE_ICON[tone] : icon;
  return (
    <span className={cn(statusBadgeVariants({ tone, variant }), className)}>
      {Icon ? <Icon aria-hidden="true" className="size-icon-sm" /> : null}
      <span>{children}</span>
    </span>
  );
}
